import { cloneElement, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ListboxButton } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ExclamationCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';

import { classNames } from 'support/helpers/generic/generic';
import { FieldState } from 'components/Form/types';

import { dropdownClasses } from '../styles';
import { DropdownSize } from '../types';

type DropdownButtonProps = {
  selectedOptions: Array<{ label: string }>;
  emptySelectionLabel?: string;
  showSelection?: boolean;
  size: DropdownSize;
  buttonClassName?: string;
  fieldState?: FieldState;
  open: boolean;
  leftIcon?: ReactElement;
  rightIcon?: ReactElement;
  disabled?: boolean;
  name?: string;
  referenceProps: any;
  onBlur: React.FocusEventHandler<HTMLButtonElement>;
  onFocus?: () => void;
  displayTestId?: string;
};

export function DropdownButton({
  selectedOptions,
  emptySelectionLabel,
  showSelection = true,
  size = 'base',
  buttonClassName,
  fieldState = 'default',
  open,
  leftIcon,
  rightIcon,
  name,
  referenceProps,
  onBlur,
  onFocus,
  displayTestId,
}: DropdownButtonProps) {
  const { t } = useTranslation();

  // Determine what text to display in the button
  const buttonText =
    !showSelection && emptySelectionLabel
      ? emptySelectionLabel || t('components.select.pleaseChoose')
      : selectedOptions.length
        ? selectedOptions.map((option) => t(option.label)).join(', ')
        : emptySelectionLabel || t('components.select.pleaseChoose');

  return (
    <ListboxButton
      className={classNames(
        {
          'border-gray-300 active:border-procuros-green-500 focus-visible:border-procuros-green-500':
            fieldState === 'default',
          'border-red-500': fieldState === 'error',
          'focus-visible:ring-2 focus-visible:ring-red-500 focus:ring-2 focus:ring-red-500':
            !open && fieldState === 'error',
          'ring-2 ring-red-500': open && fieldState === 'error',
          'border-blue-500': fieldState === 'pending-validation',
          'focus-visible:ring-2 focus-visible:ring-blue-500 focus:ring-2 focus:ring-blue-500':
            !open && fieldState === 'pending-validation',
          'ring-2 ring-blue-500': open && fieldState === 'pending-validation',
          'border-procuros-green-500': open && fieldState === 'default',
          'text-gray-500': !selectedOptions.length,
        },
        'disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 overflow-hidden flex items-center relative flex-1 min-w-0 bg-white border shadow-sm text-left cursor-default focus-visible:outline-none active:outline-none active:ring-0 focus-visible:ring-0',
        dropdownClasses.button.size[size],
        buttonClassName ?? 'rounded-md',
      )}
      data-name={name}
      data-field={name}
      {...referenceProps}
      onBlur={onBlur}
      onFocus={onFocus}
    >
      {leftIcon &&
        cloneElement(leftIcon, {
          ...leftIcon.props,
          className: classNames(dropdownClasses.leftIcon.size[size], leftIcon.props.className),
        })}
      <span className="block truncate" data-testid={displayTestId}>
        {buttonText}
      </span>

      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
        {rightIcon &&
          cloneElement(rightIcon, {
            ...rightIcon.props,
            className: classNames(dropdownClasses.rightIcon.size[size], rightIcon.props.className),
          })}
        {fieldState === 'error' && (
          <ExclamationCircleIcon
            className={classNames(dropdownClasses.rightIcon.size[size], 'bg-white text-red-500')}
            aria-hidden="true"
          />
        )}
        {fieldState === 'pending-validation' && (
          <MinusCircleIcon
            className={classNames(dropdownClasses.rightIcon.size[size], 'bg-white text-blue-500')}
            aria-hidden="true"
          />
        )}
        <ChevronDownIcon className="size-5 text-gray-400" aria-hidden="true" />
      </span>
    </ListboxButton>
  );
}
